import { useTranslation } from 'react-i18next';

interface PaginationProps {
  page: number; // 1-based
  pageCount: number;
  total: number;
  pageSize: number;
  onPage: (page: number) => void;
}

function pageList(page: number, pageCount: number): (number | '…')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const pages = new Set<number>([1, pageCount, page, page - 1, page + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push('…');
    out.push(p);
    prev = p;
  }
  return out;
}

export function Pagination({ page, pageCount, total, pageSize, onPage }: PaginationProps) {
  const { t } = useTranslation();
  if (pageCount <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const btn =
    'inline-flex h-9 min-w-9 items-center justify-center rounded-lg px-2.5 text-sm font-semibold transition disabled:opacity-40 disabled:pointer-events-none';

  return (
    <div className="flex flex-col items-center justify-between gap-3 pt-1 sm:flex-row">
      <span className="text-xs text-conduril-muted">{t('pagination.showing', { from, to, total })}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className={`${btn} border border-conduril-border bg-white hover:bg-conduril-surface`}
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label={t('pagination.prev')}
        >
          ‹
        </button>
        {pageList(page, pageCount).map((p, i) =>
          p === '…' ? (
            <span key={`e${i}`} className="px-1.5 text-conduril-muted">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPage(p)}
              aria-current={p === page}
              className={`${btn} ${
                p === page
                  ? 'bg-conduril text-white shadow-btn'
                  : 'border border-conduril-border bg-white hover:bg-conduril-surface'
              }`}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className={`${btn} border border-conduril-border bg-white hover:bg-conduril-surface`}
          onClick={() => onPage(page + 1)}
          disabled={page >= pageCount}
          aria-label={t('pagination.next')}
        >
          ›
        </button>
      </div>
    </div>
  );
}
